/**
 * Activity — the organisation's running record of what happened to its work:
 * uploads, extractions, edits, publishes, cancellations and ERP postings.
 *
 * Read as a timeline. Entries are grouped under the same date labels the
 * dashboard uses (Today, Yesterday, This Week…), each group's header sticks
 * under the top bar while you scroll through it, and a rail joins the entries
 * so the order reads at a glance. Entries that point at a request or document
 * are real links, so they open with the keyboard and in a new tab.
 *
 * Search applies on Enter, not per keystroke — the feed is assembled from
 * several queries server-side, and a request per keypress would be wasteful.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import { dashboardApi } from '../services/api'
import { formatTimeAgo, formatDateTime, getDateGroupLabel } from '../utils/dateUtils'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  AlertCircle,
  Upload,
  Cpu,
  Edit3,
  Plus,
  Activity as ActivityIcon,
  RefreshCw,
  Building2,
  FileText,
  SearchX,
  X,
  Ban,
  Layers,
} from 'lucide-react'

const TYPE_OPTIONS = [
  { value: 'all', label: 'All Activity' },
  { value: 'publish', label: 'Published' },
  { value: 'cancel', label: 'Cancelled' },
  { value: 'upload', label: 'Uploads' },
  { value: 'extraction', label: 'Extractions' },
  { value: 'edit', label: 'Edits' },
  { value: 'new_request', label: 'New Requests' },
  { value: 'bc_post', label: 'Business Central' },
  { value: 'ns_post', label: 'NetSuite' },
]

const PAGE_SIZES = [10, 25, 50]

/** Rendered in place of an icon for NetSuite, matching the NS badges elsewhere. */
function NetSuiteGlyph({ className = '' }) {
  return <span aria-hidden="true" className={`flex h-4 w-4 items-center justify-center text-[10px] font-bold leading-none tracking-tight ${className}`}>NS</span>
}

// Class strings are spelled out in full so Tailwind can see them.
//   node  — the timeline marker
//   badge — the type pill on the right of the entry
//   chip  — icon colour in the filter bar when that type is selected
const ACTIVITY_META = {
  publish: {
    icon: Check, label: 'Published',
    node: 'bg-emerald-50 text-emerald-600 border-emerald-200/80 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-400/20',
    badge: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20',
    chip: 'text-emerald-600 dark:text-emerald-300',
  },
  cancel: {
    icon: Ban, label: 'Cancelled',
    node: 'bg-rose-50 text-rose-600 border-rose-200/80 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-400/20',
    badge: 'bg-rose-50 text-rose-700 ring-rose-600/15 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-400/20',
    chip: 'text-rose-600 dark:text-rose-300',
  },
  upload: {
    icon: Upload, label: 'Upload',
    node: 'bg-sky-50 text-sky-600 border-sky-200/80 dark:bg-sky-500/10 dark:text-sky-300 dark:border-sky-400/20',
    badge: 'bg-sky-50 text-sky-700 ring-sky-600/15 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-400/20',
    chip: 'text-sky-600 dark:text-sky-300',
  },
  extraction: {
    icon: Cpu, label: 'Extraction',
    node: 'bg-violet-50 text-violet-600 border-violet-200/80 dark:bg-violet-500/10 dark:text-violet-300 dark:border-violet-400/20',
    badge: 'bg-violet-50 text-violet-700 ring-violet-600/15 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/20',
    chip: 'text-violet-600 dark:text-violet-300',
  },
  edit: {
    icon: Edit3, label: 'Edit',
    node: 'bg-amber-50 text-amber-600 border-amber-200/80 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-400/20',
    badge: 'bg-amber-50 text-amber-700 ring-amber-600/15 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20',
    chip: 'text-amber-600 dark:text-amber-300',
  },
  new_request: {
    icon: Plus, label: 'New Request',
    node: 'bg-navy-50 text-navy-600 border-navy-200/80 dark:bg-navy-500/10 dark:text-navy-300 dark:border-navy-400/20',
    badge: 'bg-navy-50 text-navy-700 ring-navy-600/15 dark:bg-navy-500/10 dark:text-navy-300 dark:ring-navy-400/20',
    chip: 'text-navy-600 dark:text-navy-300',
  },
  bc_post: {
    icon: Building2, label: 'Business Central',
    node: 'bg-teal-50 text-teal-600 border-teal-200/80 dark:bg-teal-500/10 dark:text-teal-300 dark:border-teal-400/20',
    badge: 'bg-teal-50 text-teal-700 ring-teal-600/15 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/20',
    chip: 'text-teal-600 dark:text-teal-300',
  },
  ns_post: {
    icon: NetSuiteGlyph, label: 'NetSuite',
    node: 'bg-purple-50 text-purple-600 border-purple-200/80 dark:bg-purple-500/10 dark:text-purple-300 dark:border-purple-400/20',
    badge: 'bg-purple-50 text-purple-700 ring-purple-600/15 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-400/20',
    chip: 'text-purple-600 dark:text-purple-300',
  },
}

const DEFAULT_META = {
  icon: ActivityIcon, label: 'Activity',
  node: 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700',
  badge: 'bg-gray-50 text-gray-700 ring-gray-500/15 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-600/30',
  chip: 'text-navy-600 dark:text-navy-300',
}

const getActivityMeta = (type) => ACTIVITY_META[type] || DEFAULT_META

/** Uploads open the document; everything else is about a request. */
const getActivityLink = (item) => {
  if (!item.entity_id) return null
  if (item.type === 'upload') return `/documents/${item.entity_id}`
  return `/requests/${item.entity_id}`
}

export default function Activity() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [perPage, setPerPage] = useState(10)
  const listTopRef = useRef(null)

  const fetchActivity = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = { page, page_size: perPage }
      if (search) params.search = search
      if (typeFilter !== 'all') params.type = typeFilter
      const res = await dashboardApi.getActivityPaginated(params)
      const data = res.data
      setItems(data.items || [])
      setTotal(data.total || 0)
      setTotalPages(data.total_pages || 1)
    } catch (err) {
      console.error('Failed to load activity:', err)
      setItems([])
      setError('Activity could not be loaded.')
    } finally {
      setLoading(false)
      setHasLoaded(true)
    }
  }, [page, search, typeFilter, perPage])

  useEffect(() => {
    fetchActivity()
  }, [fetchActivity])

  // Reset to page 1 on filter/search change
  const handleSearch = (e) => {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput)
  }

  const handleTypeChange = (val) => {
    setTypeFilter(val)
    setPage(1)
  }

  const handleClearSearch = () => {
    setSearchInput('')
    setSearch('')
    setPage(1)
  }

  const handleClearAll = () => {
    handleClearSearch()
    setTypeFilter('all')
  }

  // A new page starts at its top, not wherever the last one was scrolled to.
  const goToPage = (next) => {
    setPage(next)
    const top = listTopRef.current?.getBoundingClientRect().top
    if (top != null && top < 64) {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      window.scrollTo({ top: window.scrollY + top - 80, behavior: reducedMotion ? 'auto' : 'smooth' })
    }
  }

  // Pagination helpers
  const getPageNumbers = () => {
    const pages = []
    const maxVisible = 7
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      pages.push(1)
      if (page > 3) pages.push('...')
      const start = Math.max(2, page - 1)
      const end = Math.min(totalPages - 1, page + 1)
      for (let i = start; i <= end; i++) pages.push(i)
      if (page < totalPages - 2) pages.push('...')
      pages.push(totalPages)
    }
    return pages
  }

  // Consecutive entries sharing a date label form one group.
  const groups = useMemo(() => {
    const result = []
    items.forEach((item, idx) => {
      const label = getDateGroupLabel(item.time)
      const last = result[result.length - 1]
      if (last && last.label === label) last.entries.push({ item, idx })
      else result.push({ label, entries: [{ item, idx }] })
    })
    return result
  }, [items])

  // Replays the entrance when the result set changes, not on a refresh that
  // returns the same entries.
  const listKey = useMemo(
    () => items.map((item, idx) => `${item.type}-${item.entity_id}-${item.time}-${idx}`).join('|'),
    [items],
  )

  const refetching = loading && hasLoaded
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1
  const rangeEnd = Math.min(page * perPage, total)
  const typeLabel = TYPE_OPTIONS.find(o => o.value === typeFilter)?.label

  return (
    <div className="space-y-6 animate-view-in">
      <PageHeader total={hasLoaded ? total : null} />

      <div className="rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-gray-800 dark:bg-gray-900">
        {/* Toolbar */}
        <div className="border-b border-gray-100 px-4 py-3.5 dark:border-gray-800 sm:px-5">
          <div className="flex items-center gap-2">
            <SearchBox
              value={searchInput}
              applied={search}
              onChange={setSearchInput}
              onSubmit={handleSearch}
              onClear={handleClearSearch}
              busy={loading && Boolean(search)}
            />
            <TypeMenu value={typeFilter} onChange={handleTypeChange} />
            <button
              type="button"
              onClick={fetchActivity}
              disabled={loading}
              aria-label="Refresh activity"
              title="Refresh"
              className="ml-auto inline-flex h-9 flex-shrink-0 items-center gap-2 rounded-lg border border-gray-200 px-2.5 text-[13px] font-medium text-gray-600 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100 sm:px-3"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Summary line */}
        <div ref={listTopRef} className="flex min-h-[2.75rem] flex-wrap items-center gap-x-2 gap-y-1.5 px-4 pt-3 text-sm text-gray-500 dark:text-gray-400 sm:px-5">
          {!hasLoaded ? (
            <div className="skeleton h-4 w-44" />
          ) : total > 0 ? (
            <span>
              Showing <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{rangeStart}&ndash;{rangeEnd}</span> of{' '}
              <span className="font-medium tabular-nums text-gray-900 dark:text-gray-100">{total}</span> activit{total !== 1 ? 'ies' : 'y'}
            </span>
          ) : (
            <span>No activity</span>
          )}
          {search && (
            <FilterPill label={<>Matching &ldquo;{search}&rdquo;</>} onRemove={handleClearSearch} removeLabel="Clear search" />
          )}
          {typeFilter !== 'all' && (
            <FilterPill label={typeLabel} onRemove={() => handleTypeChange('all')} removeLabel={`Remove ${typeLabel} filter`} />
          )}
          {search && typeFilter !== 'all' && (
            <button
              type="button"
              onClick={handleClearAll}
              className="text-xs font-medium text-gray-500 underline-offset-2 transition-colors hover:text-gray-800 hover:underline dark:text-gray-400 dark:hover:text-gray-200"
            >
              Clear all
            </button>
          )}
        </div>

        {error && (
          <div role="alert" className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 animate-panel-in dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300 sm:mx-5">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button type="button" onClick={fetchActivity} className="font-semibold hover:underline">Retry</button>
          </div>
        )}

        {/* Refetch over existing results: a sliver of progress, and the
            timeline dims instead of blanking. */}
        <div className="relative mt-3 h-0.5 overflow-hidden" aria-hidden="true">
          {refetching && (
            <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />
          )}
        </div>

        <div aria-busy={loading} className={`transition-opacity duration-200 ${refetching ? 'opacity-60' : 'opacity-100'}`}>
          {!hasLoaded ? (
            <TimelineSkeleton />
          ) : items.length === 0 ? (
            !error && <EmptyState search={search} typeLabel={typeFilter !== 'all' ? typeLabel : null} onClear={handleClearAll} />
          ) : (
            <div key={listKey}>
              {groups.map((group, groupIdx) => (
                <section key={`${group.label}-${groupIdx}`} aria-label={group.label}>
                  <DateHeader label={group.label} count={group.entries.length} />
                  <ol className="py-2">
                    {group.entries.map(({ item, idx }, i) => (
                      <ActivityRow
                        key={`${item.type}-${item.entity_id}-${idx}`}
                        item={item}
                        index={idx}
                        first={i === 0}
                        last={i === group.entries.length - 1}
                      />
                    ))}
                  </ol>
                </section>
              ))}
            </div>
          )}
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          perPage={perPage}
          pageNumbers={getPageNumbers()}
          onPage={goToPage}
          onPerPage={(size) => {
            setPerPage(size)
            setPage(1)
          }}
        />
      </div>
    </div>
  )
}

// ── Header & toolbar ─────────────────────────────────────────────────────────

function PageHeader({ total }) {
  return (
    <div className="animate-header-in">
      <div className="flex items-center gap-3.5">
        <div className="hidden h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10 sm:flex">
          <ActivityIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Activity Log</h1>
            {total != null && (
              <span key={total} className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-navy-700 ring-1 ring-inset ring-navy-200 animate-panel-in dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                {total}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Uploads, extractions, edits and approvals across your organisation, newest first.
          </p>
        </div>
      </div>
    </div>
  )
}

function SearchBox({ value, applied, onChange, onSubmit, onClear, busy }) {
  const inputRef = useRef(null)
  const [focused, setFocused] = useState(false)
  const pending = value.trim() !== '' && value !== applied

  // "/" jumps to search from anywhere on the page, as in the other list views.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      if (e.target.closest?.('input, textarea, select, [contenteditable="true"]')) return
      e.preventDefault()
      inputRef.current?.focus()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <form onSubmit={onSubmit} role="search" className="relative min-w-0 flex-1 lg:max-w-md">
      <Search className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ${focused ? 'text-navy-500 dark:text-navy-300' : 'text-gray-400'}`} />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (value || applied) onClear()
            else e.currentTarget.blur()
          }
        }}
        placeholder="Search activity…"
        aria-label="Search activity"
        className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-20 text-sm text-gray-900 transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 [&::-webkit-search-cancel-button]:hidden"
      />
      <div className="absolute inset-y-0 right-2 flex items-center gap-1">
        {busy && <RefreshCw className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden="true" />}
        {pending && (
          <kbd className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium leading-5 text-gray-500 animate-panel-in dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400 sm:inline-block">
            Enter
          </kbd>
        )}
        {value || applied ? (
          <button
            type="button"
            onClick={() => { onClear(); inputRef.current?.focus() }}
            aria-label="Clear search"
            className="rounded-md p-1 text-gray-400 transition-colors animate-panel-in hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <kbd className={`hidden rounded border border-gray-200 bg-gray-50 px-1.5 font-sans text-[11px] font-medium leading-5 text-gray-400 transition-opacity duration-200 dark:border-gray-700 dark:bg-gray-900 sm:inline-block ${focused ? 'opacity-0' : 'opacity-100'}`}>
            /
          </kbd>
        )}
      </div>
    </form>
  )
}

/**
 * Activity type picker: one button beside the search, a listbox beneath it.
 * Arrow keys move through the types, Enter or Space picks, Escape closes and
 * returns focus to the button.
 */
function TypeMenu({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const listRef = useRef(null)
  const current = TYPE_OPTIONS.find(o => o.value === value) || TYPE_OPTIONS[0]
  const filtered = value !== 'all'
  const CurrentIcon = filtered ? getActivityMeta(value).icon : Layers

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open])

  useEffect(() => {
    if (open) listRef.current?.querySelector('[aria-selected="true"]')?.focus()
  }, [open])

  const close = () => {
    setOpen(false)
    buttonRef.current?.focus()
  }

  const onListKeyDown = (e) => {
    const options = Array.from(listRef.current?.querySelectorAll('[role="option"]') || [])
    const idx = options.indexOf(document.activeElement)
    if (e.key === 'Escape') { e.preventDefault(); close() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); options[(idx + 1) % options.length]?.focus() }
    else if (e.key === 'ArrowUp') { e.preventDefault(); options[(idx - 1 + options.length) % options.length]?.focus() }
    else if (e.key === 'Home') { e.preventDefault(); options[0]?.focus() }
    else if (e.key === 'End') { e.preventDefault(); options[options.length - 1]?.focus() }
    else if (e.key === 'Tab') setOpen(false)
  }

  return (
    <div ref={rootRef} className="relative flex-shrink-0">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true) } }}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Filter by activity type: ${current.label}`}
        className={`inline-flex h-9 items-center gap-2 rounded-lg border px-3 text-[13px] font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 ${
          open || filtered
            ? 'border-navy-300 bg-navy-50/60 text-gray-900 dark:border-navy-500/40 dark:bg-navy-500/10 dark:text-gray-100'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100'
        }`}
      >
        <CurrentIcon className={`h-3.5 w-3.5 ${filtered ? getActivityMeta(value).chip : 'text-gray-400'}`} />
        <span className="hidden sm:inline">{filtered ? current.label : 'All types'}</span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-400 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          aria-label="Activity type"
          onKeyDown={onListKeyDown}
          className="absolute left-0 z-30 mt-2 w-60 origin-top-left rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl ring-1 ring-black/5 animate-fade-in dark:border-gray-700 dark:bg-gray-800"
        >
          {TYPE_OPTIONS.map(option => {
            const selected = option.value === value
            const meta = option.value === 'all' ? null : getActivityMeta(option.value)
            const Icon = meta ? meta.icon : Layers
            return (
              <li key={option.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => { onChange(option.value); close() }}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left text-sm transition-colors focus:outline-none ${
                    selected
                      ? 'bg-navy-50 font-medium text-navy-700 dark:bg-navy-500/15 dark:text-navy-200'
                      : 'text-gray-700 hover:bg-gray-50 focus-visible:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60 dark:focus-visible:bg-gray-700/60'
                  }`}
                >
                  <span className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border ${meta ? meta.node : DEFAULT_META.node}`}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <span className="flex-1">{option.label}</span>
                  {selected && <Check className="h-3.5 w-3.5" />}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

function FilterPill({ label, onRemove, removeLabel }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-navy-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-navy-700 ring-1 ring-inset ring-navy-200/70 animate-panel-in dark:bg-navy-500/10 dark:text-navy-200 dark:ring-navy-400/20">
      <span className="max-w-[16rem] truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className="rounded-full p-0.5 transition-colors hover:bg-navy-100 dark:hover:bg-navy-400/20"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  )
}

// ── Timeline ─────────────────────────────────────────────────────────────────

/** Sticks under the app's 4rem top bar while its group is on screen. */
function DateHeader({ label, count }) {
  return (
    <div className="sticky top-16 z-10 flex items-center gap-2 border-y border-gray-100 bg-gray-50/90 px-4 py-2 backdrop-blur-sm supports-[backdrop-filter]:bg-gray-50/75 dark:border-gray-800 dark:bg-gray-900/90 dark:supports-[backdrop-filter]:bg-gray-900/75 sm:px-5">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</h2>
      <span className="text-[11px] font-medium tabular-nums text-gray-400 dark:text-gray-500">{count}</span>
    </div>
  )
}

// The rail runs through the centre of the 2.25rem markers: row padding
// (1rem, or 1.25rem from sm) plus half a marker, less half the rail's width.
// Markers are centred on their row, so the first and last segments stop at the row's midline.
const RAIL = 'pointer-events-none absolute left-[calc(2.125rem-0.5px)] w-px bg-gray-200 dark:bg-gray-700/80 sm:left-[calc(2.375rem-0.5px)]'

function ActivityRow({ item, index, first, last }) {
  const meta = getActivityMeta(item.type)
  const Icon = meta.icon
  const link = getActivityLink(item)
  const Wrapper = link ? Link : 'div'
  const wrapperProps = link ? { to: link } : {}

  const showBc = item.bc_enabled || item.template_bc_enabled || item.type === 'bc_post'
  const showNs = item.ns_enabled || item.template_ns_enabled || item.type === 'ns_post'

  return (
    <li
      className="relative animate-rise-in"
      style={{ '--rise-delay': `${Math.min(index, 14) * 28}ms` }}
    >
      {!(first && last) && (
        <span
          aria-hidden="true"
          className={`${RAIL} ${first ? 'bottom-0 top-1/2' : last ? 'bottom-1/2 top-0' : 'inset-y-0'}`}
        />
      )}
      <Wrapper
        {...wrapperProps}
        className={`group relative flex items-center gap-3 px-4 py-1.5 focus:outline-none sm:gap-4 sm:px-5`}
      >
        {/* Opaque base under the tint: the dark-mode tints are translucent and
            would otherwise let the rail show through the marker. */}
        <span
          className={`relative z-[1] h-9 w-9 flex-shrink-0 rounded-full bg-white ring-4 ring-white transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] dark:bg-gray-900 dark:ring-gray-900 ${link ? 'group-hover:scale-110' : ''}`}
        >
          <span className={`flex h-full w-full items-center justify-center rounded-full border ${meta.node}`}>
            <Icon className="h-4 w-4" />
          </span>
        </span>

        <div
          className={`flex min-w-0 flex-1 items-start gap-3 rounded-xl px-3 py-2.5 transition-colors duration-200 ${
            link
              ? 'group-hover:bg-gray-50 group-focus-visible:bg-navy-50/60 group-focus-visible:ring-2 group-focus-visible:ring-navy-500/40 dark:group-hover:bg-gray-800/60 dark:group-focus-visible:bg-navy-500/10'
              : ''
          }`}
        >
          <div className="min-w-0 flex-1">
            <p className="break-words text-sm leading-6 text-gray-900 dark:text-gray-100">{item.title}</p>

            {(showBc || showNs || item.bc_po_number || item.ns_po_number) && (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {showBc && (
                  <IntegrationTag className="bg-teal-50 text-teal-700 ring-teal-600/15 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/20">
                    <Building2 className="h-3 w-3" /> Business Central
                  </IntegrationTag>
                )}
                {showNs && (
                  <IntegrationTag className="bg-purple-50 text-purple-700 ring-purple-600/15 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-400/20">
                    NS
                  </IntegrationTag>
                )}
                {item.bc_po_number && (
                  <IntegrationTag className="bg-teal-50 text-teal-700 ring-teal-600/15 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-400/20">
                    <FileText className="h-3 w-3" /> PO: <span className="tabular-nums">{item.bc_po_number}</span>
                  </IntegrationTag>
                )}
                {item.ns_po_number && (
                  <IntegrationTag className="bg-purple-50 text-purple-700 ring-purple-600/15 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-400/20">
                    <FileText className="h-3 w-3" /> PO: <span className="tabular-nums">{item.ns_po_number}</span>
                  </IntegrationTag>
                )}
              </div>
            )}

            <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-xs text-gray-500 dark:text-gray-400">
              <time dateTime={item.time || undefined}>{formatTimeAgo(item.time)}</time>
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">&middot;</span>
              <span className="tabular-nums">{formatDateTime(item.time)}</span>
              {/* On a phone the type pill gives way to the text; the marker still carries the colour. */}
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-600 sm:hidden">&middot;</span>
              <span className="sm:hidden">{meta.label}</span>
            </p>
          </div>

          <div className="hidden flex-shrink-0 items-center gap-2 self-center sm:flex">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.badge}`}>
              {meta.label}
            </span>
            {link && (
              <ChevronRight
                aria-hidden="true"
                className="h-4 w-4 text-gray-400 opacity-0 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:-translate-x-1 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:translate-x-0 group-focus-visible:opacity-100 dark:text-gray-500"
              />
            )}
          </div>
        </div>
      </Wrapper>
    </li>
  )
}

function IntegrationTag({ className, children }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${className}`}>
      {children}
    </span>
  )
}

function EmptyState({ search, typeLabel, onClear }) {
  const filtered = Boolean(search || typeLabel)
  return (
    <div className="px-6 py-16 text-center animate-panel-in">
      <div className="relative mx-auto h-20 w-20">
        <div className="absolute inset-0 rotate-6 rounded-2xl bg-navy-100/60 dark:bg-navy-500/10" />
        <div className="absolute inset-0 -rotate-3 rounded-2xl bg-white shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700" />
        <div className="absolute inset-0 flex items-center justify-center">
          {filtered
            ? <SearchX className="h-8 w-8 text-navy-500 dark:text-navy-300" />
            : <ActivityIcon className="h-8 w-8 text-navy-500 dark:text-navy-300" />}
        </div>
      </div>
      <h3 className="mt-5 text-base font-semibold text-gray-900 dark:text-gray-100">
        {filtered ? 'No matching activity' : 'No activity yet'}
      </h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
        {search && typeLabel
          ? <>Nothing in {typeLabel} matches &ldquo;{search}&rdquo;. Try a broader term, or look across all activity.</>
          : search
            ? <>Nothing matches &ldquo;{search}&rdquo;. Try a request title, a file name or a person&rsquo;s name.</>
            : typeLabel
              ? <>Nothing has been recorded under {typeLabel} yet.</>
              : 'Uploads, extractions, edits and approvals will appear here as your team works.'}
      </p>
      {filtered && (
        <button
          type="button"
          onClick={onClear}
          className="mt-5 inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-700 transition-all duration-200 hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-sm active:translate-y-0 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
        >
          <X className="h-3.5 w-3.5" />
          Clear filters
        </button>
      )}
    </div>
  )
}

// ── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, totalPages, perPage, pageNumbers, onPage, onPerPage }) {
  return (
    <div className="flex flex-col gap-3 border-t border-gray-100 px-4 py-3.5 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400 sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <label className="flex items-center gap-2">
        Rows per page
        <select
          value={perPage}
          onChange={(e) => onPerPage(Number(e.target.value))}
          className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-sm text-gray-700 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          {PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
        </select>
      </label>
      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="hidden sm:inline">Previous</span>
          </button>
          {pageNumbers.map((p, i) => (
            <React.Fragment key={`${p}-${i}`}>
              {p === '...' ? (
                <span className="px-1.5 text-gray-400 dark:text-gray-500">…</span>
              ) : (
                <button
                  type="button"
                  onClick={() => onPage(p)}
                  aria-current={p === page ? 'page' : undefined}
                  className={`h-8 min-w-[2rem] rounded-lg px-2 text-sm font-medium tabular-nums transition-all duration-200 ${
                    p === page
                      ? 'bg-navy-800 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100 active:scale-95 dark:text-gray-400 dark:hover:bg-gray-800'
                  }`}
                >
                  {p}
                </button>
              )}
            </React.Fragment>
          ))}
          <button
            type="button"
            onClick={() => onPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="inline-flex h-8 items-center gap-1 rounded-lg px-2.5 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </div>
  )
}

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SKELETON_WIDTHS = ['w-72', 'w-56', 'w-80', 'w-64', 'w-48', 'w-60']

function TimelineSkeleton() {
  return (
    <div aria-hidden="true">
      <div className="border-y border-gray-100 bg-gray-50/90 px-4 py-2.5 dark:border-gray-800 dark:bg-gray-900/90 sm:px-5">
        <div className="skeleton h-3 w-16" />
      </div>
      <ol className="py-2">
        {SKELETON_WIDTHS.map((width, i) => (
          <li key={i} className="relative">
            <span className={`${RAIL} ${i === 0 ? 'bottom-0 top-1/2' : i === SKELETON_WIDTHS.length - 1 ? 'bottom-1/2 top-0' : 'inset-y-0'}`} />
            <div className="flex items-center gap-3 px-4 py-1.5 sm:gap-4 sm:px-5">
              <div className="relative z-[1] h-9 w-9 flex-shrink-0 rounded-full ring-4 ring-white dark:ring-gray-900">
                <div className="skeleton h-9 w-9 rounded-full" />
              </div>
              <div className="flex flex-1 items-center gap-3 px-3 py-2.5">
                <div className="flex-1 space-y-2">
                  <div className={`skeleton h-4 max-w-full ${width}`} />
                  <div className="skeleton h-3 w-40" />
                </div>
                <div className="skeleton hidden h-5 w-20 rounded-full sm:block" />
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  )
}
